import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, ChevronUp } from 'lucide-react';
import './css/LocationPicker.css';

const LocationPicker = ({ value, onChange, placeholder = "Search for a location..." }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isLocationDetailsCollapsed, setIsLocationDetailsCollapsed] = useState(false);
  const searchTimeoutRef = useRef(null);
  const justSelectedRef = useRef(false);

  // Initialize with existing value if provided
  useEffect(() => {
    if (value) {
      if (typeof value === 'string') {
        setSearchQuery(value);
        setSelectedLocation(null);
      } else if (value.formatted_address) {
        setSearchQuery(value.street_address || value.formatted_address);
        setSelectedLocation(value);
      }
    }
  }, [value]);

  // Geocoding function using Nominatim (OpenStreetMap) — South Africa only
  const searchLocation = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=8&addressdetails=1&countrycodes=za&accept-language=en`,
        { headers: { 'User-Agent': 'BuzzTickets/1.0 (buzztickets.co.za)' } }
      );
      const data = await response.json();

      // Pull a leading house number out of whatever the user typed
      const typedHouseNumber = query.trim().match(/^(\d+[a-zA-Z]?)/)?.[1] || '';

      const formattedResults = data.map(item => {
        const addr = item.address || {};
        const houseNumber = addr.house_number || typedHouseNumber;
        const road = addr.road || addr.pedestrian || addr.footway || addr.path || '';
        const streetAddress = houseNumber && road
          ? `${houseNumber} ${road}`
          : road || addr.amenity || addr.leisure || addr.tourism || item.display_name.split(',')[0];
        const suburb = addr.suburb || addr.neighbourhood || '';
        const city = addr.city || addr.town || addr.village || addr.municipality || suburb || '';
        const province = addr.state || addr.province || '';

        return {
          display_name: item.display_name,
          lat: parseFloat(item.lat),
          lon: parseFloat(item.lon),
          address: {
            street_address: streetAddress,
            suburb,
            city,
            province_state: province,
            country: 'South Africa',
            postcode: addr.postcode || '',
          },
        };
      });

      setSearchResults(formattedResults);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error searching location:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      if (justSelectedRef.current) {
        justSelectedRef.current = false;
        return;
      }
      searchLocation(searchQuery);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Handle location selection
  const handleLocationSelect = (location) => {
    const { street_address, suburb, city, province_state, country, postcode } = location.address;
    const parts = [street_address, suburb, city, province_state].filter(Boolean);
    const formatted_address = parts.join(', ') + (postcode ? ` ${postcode}` : '') + ', South Africa';

    const locationData = {
      street_address,
      suburb,
      city,
      province_state,
      country,
      postcode,
      latitude: location.lat,
      longitude: location.lon,
      formatted_address,
    };

    justSelectedRef.current = true;
    setSelectedLocation(locationData);
    setSearchQuery(street_address || parts[0] || '');
    setSearchResults([]);
    setShowSearchResults(false);
    onChange(locationData);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setShowSearchResults(true);
  };

  // Handle input blur
  const handleInputBlur = () => {
    // Delay hiding results to allow click on result
    setTimeout(() => {
      setShowSearchResults(false);

      // Accept free-form text if user typed an address not found by Nominatim
      const currentQuery = searchQuery.trim();
      if (
        currentQuery &&
        (!selectedLocation || selectedLocation.street_address !== currentQuery)
      ) {
        const freeFormLocation = {
          street_address: currentQuery,
          city: '',
          province_state: '',
          country: '',
          latitude: null,
          longitude: null,
          formatted_address: currentQuery,
        };
        setSelectedLocation(freeFormLocation);
        onChange(freeFormLocation);
      }
    }, 200);
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (searchResults.length > 0) {
      setShowSearchResults(true);
    }
  };

  return (
    <div className="location-picker">
      <div className="location-search">
        <div className="search-input-container">
          <Search 
            className="search-icon" 
            size={16} 
            style={{ 
              position: 'absolute', 
              left: '10px', 
              zIndex: 2, 
              pointerEvents: 'none',
              color: '#666'
            }} 
          />
          <input
            type="text"
            value={searchQuery}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            className="location-search-input"
            style={{
              paddingLeft: '36px',
              paddingTop: '6px',
              paddingBottom: '6px',
              fontSize: '13px',
              minHeight: '32px'
            }}
          />
          {isSearching && <div className="search-spinner">...</div>}
        </div>
        <button 
          className="location-collapse-toggle"
          onClick={() => selectedLocation && setIsLocationDetailsCollapsed(!isLocationDetailsCollapsed)}
          title={selectedLocation ? (isLocationDetailsCollapsed ? "Show location details" : "Hide location details") : "Select a location first"}
          disabled={!selectedLocation}
          style={{
            opacity: selectedLocation ? 1 : 0.4,
            cursor: selectedLocation ? 'pointer' : 'not-allowed'
          }}
        >
          {isLocationDetailsCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </button>

        {/* Search Results Dropdown */}
        {showSearchResults && searchResults.length > 0 && (
          <div className="search-results">
            {searchResults.map((result, index) => {
              const { street_address, suburb, city, province_state, postcode } = result.address;
              const primary = street_address || city || result.display_name.split(',')[0];
              const secondary = [suburb, city, province_state, postcode].filter(Boolean).join(', ');
              return (
                <div
                  key={index}
                  className="search-result-item"
                  onClick={() => handleLocationSelect(result)}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <div className="result-name">{primary}</div>
                  {secondary && <div className="result-details">{secondary}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected Location Details */}
      {selectedLocation && !isLocationDetailsCollapsed && (
        <div className="selected-location-details">
          <div className="location-details-grid">
            {selectedLocation.street_address && (
              <div className="detail-item">
                <strong>Street:</strong> {selectedLocation.street_address}
              </div>
            )}
            {selectedLocation.suburb && (
              <div className="detail-item">
                <strong>Suburb:</strong> {selectedLocation.suburb}
              </div>
            )}
            {selectedLocation.city && (
              <div className="detail-item">
                <strong>City:</strong> {selectedLocation.city}
              </div>
            )}
            {selectedLocation.province_state && (
              <div className="detail-item">
                <strong>Province:</strong> {selectedLocation.province_state}
              </div>
            )}
            {selectedLocation.postcode && (
              <div className="detail-item">
                <strong>Postal Code:</strong> {selectedLocation.postcode}
              </div>
            )}
            {selectedLocation.latitude != null && selectedLocation.longitude != null && (
              <div className="detail-item">
                <strong>Coordinates:</strong> {selectedLocation.latitude.toFixed(6)}, {selectedLocation.longitude.toFixed(6)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
